/* BJ Beauty — Generic horizontal carousel custom element <bj-carousel>.
   Uses native CSS scroll-snap; JS handles arrow scrolling, dot pagination,
   and card-width calculation so the merchant controls "cards visible" in
   the editor without having to touch CSS. */

if (!customElements.get('bj-carousel')) {
  class BJCarousel extends HTMLElement {
    constructor() {
      super();
      this.viewport = null;
      this.track = null;
      this.cards = [];
      this.prevBtn = null;
      this.nextBtn = null;
      this.dotsContainer = null;
      this.dots = [];
      this.perPageDesktop = 4;
      this.perPageMobile = 1;
      this._resizeRaf = null;
      this._scrollRaf = null;
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-bj-track]');
      if (!this.viewport) return;
      this.track = this.viewport.querySelector('.bj-pc-carousel__track');
      if (!this.track) return;

      this.cards = Array.from(this.track.children);
      if (!this.cards.length) return;

      this.prevBtn = this.querySelector('.bj-pc-carousel__nav--prev');
      this.nextBtn = this.querySelector('.bj-pc-carousel__nav--next');
      this.dotsContainer = this.querySelector('[data-bj-dots]');

      this.perPageDesktop = parseInt(this.dataset.perPageDesktop, 10) || 4;
      this.perPageMobile = parseInt(this.dataset.perPageMobile, 10) || 1;

      this.computeLayout();
      this.buildDots();
      this.bindEvents();
      this.updateState();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
    }

    /* Decide how many cards are visible at once for the current viewport
       and write the resulting card width into a CSS variable. */
    computeLayout() {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      this.perPage = isMobile ? this.perPageMobile : this.perPageDesktop;

      const styles = getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;

      // viewport width minus (perPage - 1) gaps, divided by perPage
      const vw = this.viewport.clientWidth;
      const cardWidth = (vw - gap * (this.perPage - 1)) / this.perPage;

      this.style.setProperty('--bj-pc-card-width', `${Math.floor(cardWidth)}px`);
      this.cardWidth = cardWidth;
      this.gap = gap;
    }

    buildDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';

      // We only show dots when mobile (one card per page) — but build for any case
      const pages = Math.max(1, Math.ceil(this.cards.length / this.perPageMobile));
      for (let i = 0; i < pages; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'bj-pc-carousel__dot' + (i === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', `Go to page ${i + 1}`);
        dot.dataset.page = String(i);
        dot.addEventListener('click', () => this.goToPage(i));
        this.dotsContainer.appendChild(dot);
      }
      this.dots = Array.from(this.dotsContainer.children);
    }

    bindEvents() {
      this._onResize = () => {
        if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
        this._resizeRaf = requestAnimationFrame(() => {
          this.computeLayout();
          this.updateState();
        });
      };
      window.addEventListener('resize', this._onResize);

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.scrollByPage(-1));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.scrollByPage(1));
      }

      this.viewport.addEventListener('scroll', () => {
        if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
        this._scrollRaf = requestAnimationFrame(() => this.updateState());
      }, { passive: true });
    }

    scrollByPage(direction) {
      const step = (this.cardWidth + this.gap) * this.perPage;
      const isRTL = document.documentElement.classList.contains('rtl');
      const delta = (isRTL ? -1 : 1) * direction * step;
      this.viewport.scrollBy({ left: delta, behavior: 'smooth' });
    }

    goToPage(pageIndex) {
      const step = (this.cardWidth + this.gap) * (this.perPage || this.perPageMobile);
      const isRTL = document.documentElement.classList.contains('rtl');
      const target = (isRTL ? -1 : 1) * step * pageIndex;
      this.viewport.scrollTo({ left: target, behavior: 'smooth' });
    }

    updateState() {
      const max = this.viewport.scrollWidth - this.viewport.clientWidth - 1;
      const isRTL = document.documentElement.classList.contains('rtl');
      // In RTL, scrollLeft is negative or reversed depending on browser — use abs for thresholds
      const scrolled = Math.abs(this.viewport.scrollLeft);

      const atStart = scrolled <= 1;
      const atEnd = scrolled >= max;

      if (this.prevBtn) this.prevBtn.toggleAttribute('disabled', atStart);
      if (this.nextBtn) this.nextBtn.toggleAttribute('disabled', atEnd);

      // Update active dot based on which "page" we're closest to
      if (this.dots.length) {
        const step = (this.cardWidth + this.gap) * (this.perPage || this.perPageMobile);
        const page = Math.round(scrolled / step);
        this.dots.forEach((d, idx) => d.classList.toggle('is-active', idx === page));
      }
    }
  }

  customElements.define('bj-carousel', BJCarousel);
}